import "./CampanasPage.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import {
  CampanaContactoResponse,
  CampanaResponse,
  CampanaStatsResponse,
} from "../../../shared/response/marketing/CampanaResponse";
import { CampanaService } from "../../services/marketing/CampanaService";
import { CampanaDeleteService } from "../../services/marketing/CampanaDeleteService";
import { CampanaFormModal } from "./CampanaFormModal";
import { PipelinePage } from "../pipeline/PipelinePage";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { ESTADO_LABELS, OBJETIVO_LABELS, TIPO_LABELS } from "./campanaConstants";

const ESTADO_ORDER: CampanaResponse["estado"][] = ["activa", "borrador", "finalizada"];
const ESTADO_GROUP_LABEL: Record<string, string> = { activa: "Activas", borrador: "Borradores", finalizada: "Finalizadas" };

export class CampanasPage extends BasePageComponent {
  private _campanas: CampanaResponse[] = [];
  private _detalleContactos: CampanaContactoResponse[] = [];
  private _selected: CampanaResponse | null = null;

  private _listaView!:   HTMLElement;
  private _detalleView!: HTMLElement;

  public getAppRoute(): string { return "/marketing/campanas"; }
  public getPageTitle(): string | null { return "Marketing"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page cmp-page";
    pageContent.innerHTML = `
      <div class="cmp-content">
        <div class="cmp-view" id="cmp-view-lista">
          <div class="cmp-pg-hd">
            <div>
              <div class="cmp-pg-ey">Marketing</div>
              <div class="cmp-pg-ti">Campañas</div>
              <div class="cmp-pg-su">Captación de leads por landing con código de activación</div>
            </div>
            <button class="sx-btn sx-btn-primary sx-btn-sm" id="cmp-nuevo-btn">
              <i class="ph ph-plus" aria-hidden="true"></i> Nueva campaña
            </button>
          </div>
          <div class="cmp-kstrip" id="cmp-kstrip"></div>
          <div id="cmp-lista"></div>
        </div>

        <div class="cmp-view" id="cmp-view-detalle" style="display:none"></div>
      </div>
    `;

    this._listaView   = document.getElementById("cmp-view-lista")!;
    this._detalleView = document.getElementById("cmp-view-detalle")!;
    document.getElementById("cmp-nuevo-btn")!.onclick = () => this._onCreateClick();

    void this._loadData();
  }

  protected override beforeDispose(): void {}

  // ── Data ───────────────────────────────────────────────────────────────────
  private async _loadData(): Promise<void> {
    try {
      const [items] = await Promise.all([CampanaService.instance.get(), this._loadStats()]);
      this._campanas = items.filter((c) => !c.isEliminado);
      this._renderLista();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar campañas", (error as Error).message);
      document.getElementById("cmp-lista")!.innerHTML = `<div class="cmp-empty">Error al cargar</div>`;
    }
  }

  private async _loadStats(): Promise<void> {
    try {
      const s: CampanaStatsResponse = await CampanaService.instance.getStats();
      document.getElementById("cmp-kstrip")!.innerHTML = `
        ${this._kpi(String(s.totalCampanas), "CAMPAÑAS", "")}
        ${this._kpi(String(s.activas), "ACTIVAS", "green")}
        ${this._kpi(String(s.totalRegistros), "REGISTROS", "blue")}
        ${this._kpi(String(s.demosActivadas), "DEMOS ACTIVADAS", "green")}
      `;
    } catch { /* KPIs opcionales */ }
  }

  private _kpi(value: string, label: string, color: string): string {
    return `<div class="cmp-kcard"><div class="cmp-k-val ${color}">${value}</div><div class="cmp-k-lbl">${label}</div></div>`;
  }

  // ── Lista ──────────────────────────────────────────────────────────────────
  private _renderLista(): void {
    const cont = document.getElementById("cmp-lista")!;
    if (this._campanas.length === 0) {
      cont.innerHTML = `<div class="cmp-empty">Sin campañas. Crea la primera con "Nueva campaña".</div>`;
      return;
    }

    cont.innerHTML = ESTADO_ORDER.map((estado) => {
      const grupo = this._campanas.filter((c) => c.estado === estado);
      if (grupo.length === 0) return "";
      return `
        <div class="cmp-sec-lbl">${ESTADO_GROUP_LABEL[estado]}</div>
        <div class="cmp-grid">${grupo.map((c) => this._cardHtml(c)).join("")}</div>`;
    }).join("");

    cont.querySelectorAll<HTMLElement>(".cmp-card").forEach((card) => {
      card.onclick = () => this._openDetalle(card.dataset.id!);
    });
  }

  private _cardHtml(c: CampanaResponse): string {
    const cupos = c.cuposMaximos > 0 ? `${c.cuposUsados}<span>/${c.cuposMaximos}</span>` : `${c.cuposUsados}<span> reg.</span>`;
    const disponibles = c.cuposMaximos > 0 ? Math.max(0, c.cuposMaximos - c.cuposUsados) : null;
    return `
      <div class="cmp-card" data-id="${c.id}">
        <div class="cmp-card-hd">
          <div class="cmp-card-name">${this._esc(c.nombre)}</div>
          ${this._estadoPill(c.estado)}
        </div>
        <div class="cmp-card-obj">${this._esc(OBJETIVO_LABELS[c.objetivo] ?? c.objetivo)}</div>
        <div class="cmp-card-meta">
          <span class="cmp-pill tipo">${this._esc(TIPO_LABELS[c.tipo] ?? c.tipo)}</span>
          ${c.codigo ? `<span class="cmp-pill code">${this._esc(c.codigo)}</span>` : `<span class="cmp-pill na">Sin código</span>`}
        </div>
        <div class="cmp-card-stats">
          <div class="cmp-cs"><div class="cmp-cs-v">${cupos}</div><div class="cmp-cs-l">Registros</div></div>
          <div class="cmp-cs"><div class="cmp-cs-v">${disponibles ?? "∞"}</div><div class="cmp-cs-l">Disponibles</div></div>
          <div class="cmp-cs"><div class="cmp-cs-v">${c.activaDemo ? "Sí" : "No"}</div><div class="cmp-cs-l">Demo</div></div>
        </div>
      </div>`;
  }

  // ── Detalle ────────────────────────────────────────────────────────────────
  private async _openDetalle(id: string): Promise<void> {
    this._selected = this._campanas.find((c) => c.id === id) ?? null;
    this._detalleContactos = [];
    this._listaView.style.display = "none";
    this._detalleView.style.display = "block";
    this._renderDetalle();

    try {
      const det = await CampanaService.instance.getDetalle(id);
      this._selected = det.campana;
      this._detalleContactos = det.contactos ?? [];
      this._renderDetalle();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar la campaña", (error as Error).message);
    }
  }

  private _backToLista(): void {
    this._detalleView.style.display = "none";
    this._listaView.style.display = "block";
    this._renderLista();
  }

  private _renderDetalle(): void {
    const c = this._selected;
    if (!c) return;
    const usados = c.cuposUsados;
    const total = c.cuposMaximos;
    const disponibles = total > 0 ? Math.max(0, total - usados) : "∞";
    const usoPct = total > 0 ? Math.round((usados / total) * 100) : 0;
    const isLanding = c.tipo === "landing";

    // Landing → métricas de cupos; WhatsApp/Email → tarjeta con el mensaje (el envío es outbound, pendiente).
    const bloqueMedio = isLanding
      ? `<div class="cmp-met-grid">
           <div class="cmp-mc"><div class="cmp-mc-v">${total > 0 ? total : "∞"}</div><div class="cmp-mc-l">Cupos</div></div>
           <div class="cmp-mc"><div class="cmp-mc-v green">${usados}</div><div class="cmp-mc-l">Registros</div></div>
           <div class="cmp-mc"><div class="cmp-mc-v amber">${disponibles}</div><div class="cmp-mc-l">Disponibles</div></div>
           <div class="cmp-mc"><div class="cmp-mc-v">${usoPct}%</div><div class="cmp-mc-l">Uso del código</div></div>
         </div>`
      : `<div class="cmp-msg-card">
           <div class="cmp-msg-card-lbl">Mensaje</div>
           <div class="cmp-msg-card-body">${c.mensajeTemplate ? this._esc(c.mensajeTemplate) : "<span style=\"color:var(--sx-text-tertiary)\">Sin mensaje definido</span>"}</div>
           <div class="cmp-msg-card-note"><i class="ph ph-info"></i> El envío de ${this._esc(TIPO_LABELS[c.tipo] ?? c.tipo)} se habilitará cuando la integración esté lista.</div>
         </div>`;

    this._detalleView.innerHTML = `
      <div class="cmp-det-hd">
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="cmp-back"><i class="ph ph-arrow-left"></i> Campañas</button>
        <span class="cmp-det-name">${this._esc(c.nombre)}</span>
        ${this._estadoPill(c.estado)}
        <div style="flex:1"></div>
        <select class="cmp-fsel" id="cmp-estado-sel">
          ${["borrador", "activa", "finalizada"].map((e) =>
            `<option value="${e}" ${c.estado === e ? "selected" : ""}>${ESTADO_LABELS[e]}</option>`).join("")}
        </select>
        <button class="sx-btn sx-btn-secondary sx-btn-sm" id="cmp-edit"><i class="ph ph-pencil"></i> Editar</button>
        <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" id="cmp-del" title="Eliminar"><i class="ph ph-trash"></i></button>
      </div>

      <div class="cmp-det-sub">
        <span class="cmp-pill tipo">${this._esc(TIPO_LABELS[c.tipo] ?? c.tipo)}</span>
        ${c.codigo ? `<span class="cmp-pill code">${this._esc(c.codigo)}</span>` : ""}
        <span>${this._esc(OBJETIVO_LABELS[c.objetivo] ?? c.objetivo)}</span>
        ${c.fechaLimite ? `<span>· Vence ${this._fmtDate(c.fechaLimite)}</span>` : ""}
        ${c.urlLanding ? `<span>· <a href="${this._esc(c.urlLanding)}" target="_blank" rel="noopener">${this._esc(c.urlLanding)}</a></span>` : ""}
      </div>

      ${bloqueMedio}

      <div class="cmp-tbl-wrap">
        <div class="cmp-tbl-hd">Contactos registrados <span class="cmp-tbl-count">${this._detalleContactos.length}</span></div>
        <table class="cmp-tbl">
          <thead><tr><th>Cliente</th><th>Estado</th><th>Fecha registro</th><th></th></tr></thead>
          <tbody id="cmp-det-tbody">
            ${this._detalleContactos.length === 0
              ? `<tr><td colspan="4" class="cmp-empty">Sin contactos registrados aún</td></tr>`
              : this._detalleContactos.map((k) => `
                <tr>
                  <td>${this._esc(k.clienteNombre)}</td>
                  <td><span class="cmp-pill stage">${this._esc(k.stage)}</span></td>
                  <td class="cmp-fecha">${this._fmtDate(k.fechaRegistro)}</td>
                  <td><button class="cmp-verlead" data-cid="${k.clienteId}" data-oid="${k.oportunidadId}">Ver lead →</button></td>
                </tr>`).join("")}
          </tbody>
        </table>
      </div>
    `;

    document.getElementById("cmp-back")!.onclick = () => this._backToLista();
    document.getElementById("cmp-edit")!.onclick = () => this._onEditClick(c);
    document.getElementById("cmp-del")!.onclick  = () => this._onDeleteClick(c);
    (document.getElementById("cmp-estado-sel") as HTMLSelectElement).onchange = (e) =>
      this._onEstadoChange((e.target as HTMLSelectElement).value);
    this._detalleView.querySelectorAll<HTMLElement>(".cmp-verlead").forEach((btn) => {
      btn.onclick = () => this._verLead(btn.dataset.cid!, btn.dataset.oid!);
    });
  }

  // ── Acciones ───────────────────────────────────────────────────────────────
  private _onCreateClick(): void {
    new CampanaFormModal((saved) => {
      this._campanas = [saved, ...this._campanas];
      this._renderLista();
      void this._loadStats();
    }).init();
  }

  private _onEditClick(c: CampanaResponse): void {
    new CampanaFormModal((saved) => {
      this._campanas = this._campanas.map((x) => (x.id === saved.id ? saved : x));
      this._selected = saved;
      this._renderDetalle();
    }, c).init();
  }

  private async _onEstadoChange(estado: string): Promise<void> {
    if (!this._selected) return;
    try {
      const saved = await CampanaService.instance.cambiarEstado(this._selected.id, { estado });
      this._selected = saved;
      this._campanas = this._campanas.map((x) => (x.id === saved.id ? saved : x));
      this._renderDetalle();
      void this._loadStats();
    } catch (error) {
      errorMessageComponent.addError("Error al cambiar el estado", (error as Error).message);
    }
  }

  private _onDeleteClick(c: CampanaResponse): void {
    new ConfirmDialogComponent({
      title: "Eliminar campaña",
      message: `¿Eliminar la campaña "${c.nombre}"? Los leads ya registrados no se eliminan.`,
      confirmLabel: "Eliminar",
      onConfirm: async () => {
        try {
          await new CampanaDeleteService().delete(c.id);
          this._campanas = this._campanas.filter((x) => x.id !== c.id);
          this._backToLista();
          void this._loadStats();
        } catch (error) {
          errorMessageComponent.addError("Error al eliminar", (error as Error).message);
        }
      },
    }).init();
  }

  private _verLead(clienteId: string, opId: string): void {
    PipelinePage.pendingFicha = { clienteId, opId };
    document.dispatchEvent(new CustomEvent("app:navigate", { detail: "/ventas/pipeline" }));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  private _estadoPill(estado: string): string {
    const cls = estado === "activa" ? "ok" : estado === "finalizada" ? "na" : "am";
    return `<span class="cmp-pill ${cls}">${ESTADO_LABELS[estado] ?? estado}</span>`;
  }

  private _fmtDate(v: number | string | null): string {
    if (v == null || v === "") return "—";
    const ms = typeof v === "number" ? v : (/^\d+$/.test(v) ? Number(v) : Date.parse(v));
    if (isNaN(ms)) return "—";
    return new Date(ms).toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "2-digit" });
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
}
